import React, { useEffect, useMemo, useState } from 'react';
import getToltReferral from '../lib/getToltReferral';
import useCheckoutState from '@repo/checkout/hooks/useCheckoutState';

const ToltContext = React.createContext();

function ToltProvider({ children }) {
  const { query, seller } = useCheckoutState();
  const [isToltLoaded, setIsToltLoaded] = useState(false);
  const [toltReferralFromParent, setToltReferralFromParent] = useState(null);

  const hasToltEnabled = seller.integrations?.tolt?.enabled && seller.integrations?.tolt?.publicId;
  const toltPublicId = seller.integrations?.tolt?.publicId;

  const isEmbedded = useMemo(() => {
    return typeof window !== 'undefined' && window.top !== window.self;
  }, []);

  // Listen for Tolt referral from parent window
  useEffect(() => {
    if (!isEmbedded || !hasToltEnabled) {
      return;
    }

    function handleMessage(event) {
      // Validate the message origin
      if (!event.origin.includes('://') || typeof event.data !== 'object') {
        return;
      }

      const { type, payload } = event.data;

      if (type === 'checkoutpage.toltReferral' && payload?.toltReferralId) {
        console.log('Received Tolt referral from parent:', payload.toltReferralId);
        setToltReferralFromParent(payload.toltReferralId);
      }
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isEmbedded, hasToltEnabled]);

  useEffect(() => {
    let pollInterval = null;

    function loadTolt() {
      const script = document.createElement('script');
      script.src = 'https://cdn.tolt.io/tolt.js';
      script.setAttribute('data-tolt', toltPublicId);
      script.async = true;

      script.onload = () => {
        // Poll for Tolt API availability instead of using arbitrary timeout
        let pollCount = 0;
        const maxPolls = 100; // 10 seconds at 100ms intervals

        pollInterval = setInterval(() => {
          pollCount++;

          // Check if Tolt has loaded and initialized by looking for its API
          if (
            typeof window.tolt_data !== 'undefined' ||
            typeof window.tolt_referral !== 'undefined'
          ) {
            clearInterval(pollInterval);
            pollInterval = null;
            setIsToltLoaded(true);
            return;
          }

          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            pollInterval = null;
            console.warn('Tolt failed to initialize within 10 seconds');
            setIsToltLoaded(true); // Set to true anyway to prevent blocking
          }
        }, 100);
      };

      script.onerror = () => {
        console.error('Failed to load Tolt script');
        setIsToltLoaded(true); // Set to true to prevent blocking the checkout
      };

      document.body.appendChild(script);
    }

    // don't load tolt if the window is not defined
    if (typeof window === 'undefined') {
      return;
    }

    if (!hasToltEnabled) {
      return;
    }

    // don't load tolt in embedded checkouts
    if (isEmbedded) {
      return;
    }

    loadTolt();

    // Cleanup function to clear interval on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [hasToltEnabled, toltPublicId, isEmbedded]);

  const toltReferral = useMemo(() => {
    if (!hasToltEnabled) {
      return null;
    }

    // Priority 1: Tolt referral from parent window (for embedded checkouts)
    if (isEmbedded && toltReferralFromParent) {
      return toltReferralFromParent;
    }

    // Priority 2: Tolt API if loaded (for hosted checkouts)
    if (isToltLoaded) {
      const toltReferralId = getToltReferral();

      if (toltReferralId) {
        return toltReferralId;
      }
    }

    const { tolt_referral } = query;

    // Priority 3: tolt_referral from query params
    // tolt_referral is the referral ID from the Tolt cookie
    // passed through by our overlay.js in embeds and pop-ups
    if (tolt_referral) {
      return tolt_referral;
    }

    return null;
  }, [query, hasToltEnabled, isToltLoaded, isEmbedded, toltReferralFromParent]);

  return (
    <ToltContext.Provider
      value={{
        toltReferral,
      }}
    >
      {children}
    </ToltContext.Provider>
  );
}

export default function useTolt() {
  const context = React.useContext(ToltContext);

  if (context === undefined) {
    throw new Error('useTolt must be used within a ToltProvider');
  }

  return context;
}

export { ToltProvider };
